import { NodeProps, useReactFlow } from '@xyflow/react';
import { debounce } from 'lodash';
import React, { memo, useCallback, useEffect } from 'react';
import styles from '@/styles/NodeIntellica.module.css';
import NodeBaseContainer from '../base/base-container/NodeBaseContainer';
import NodeIntellicaChat from './intellica-chat/NodeIntellicaChat';
import NodeIntellicaHeader from './intellica-header/NodeIntellicaHeader';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date | string;
  interactive?: {
    type: 'buttons' | 'tree';
    options?: { label: string; value: string; description?: string; subOptions?: any[] }[];
    callback?: (response: any) => void; // Optional callback
    callbackType?: string; // Store callback type for reattachment
  };
}

export interface NodeIntellicaData {
  messages?: ChatMessage[];
  configuration?: { isAutomated: boolean; modifierprompt: string };
  label?: string;
  isMinimized?: boolean;
  isLocked?: boolean;
  assignedEmails?: string[];
  summaries?: ChatMessage[];
  conversationSummary?: string;
  selections?: Record<string, string[]>;
  documentRequest?: any; // Remove dependency on AICommand type
  statusMessage?: string;
  confidenceScore?: number;
  intentScore?: number;
  objectivesScore?: number;
  userInsights?: string;
  lastProcessed?: number;
  briefcaseOptions?: string[];
  createdDocuments?: { title: string; purpose: string }[];
  intellicaMemory?: string;
  insights?: string[];
  conversationState?: { selections: string[]; lastQuestion: string; context: Record<string, any> };
  linkedNodes?: string[];
}

const NodeIntellica: React.FC<NodeProps & { data: NodeIntellicaData }> = memo((props) => {
  const { id, data } = props;
  const { updateNodeData, addNodes, addEdges, getNode, setEdges } = useReactFlow();

  // Define the callback to be used for interactive messages
  const handleAIResponse = useCallback((response: string) => {
    const newMessage: ChatMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: response,
      timestamp: new Date(),
    };
    const updatedMessages = [...(data.messages || []), newMessage];
    syncNodeDataImmediate({ messages: updatedMessages });
    sendIntellicaCommand({ response });
  }, [data.messages]);

  const callbackMap: Record<string, (response: any) => void> = {
    ai: handleAIResponse,
  };

  const syncNodeDataDebounced = useCallback(
    debounce((newData: Partial<NodeIntellicaData>) => {
      const updatedData = { ...data, ...newData };
      if (updatedData.messages) {
        updatedData.messages = updatedData.messages.map((msg) => ({
          ...msg,
          id: msg.id || `${msg.role}-${new Date(msg.timestamp).getTime()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
          interactive: msg.interactive ? {
            ...msg.interactive,
            callback: undefined, // Remove callback for serialization
            callbackType: msg.interactive.callbackType || 'ai', // Store callback type
          } : undefined,
        }));
      }
      updateNodeData(id, updatedData);
    }, 500),
    [id, data, updateNodeData]
  );

  const syncNodeDataImmediate = useCallback(
    (newData: Partial<NodeIntellicaData>) => {
      const updatedData = { ...data, ...newData };
      if (updatedData.messages) {
        updatedData.messages = updatedData.messages.map((msg) => ({
          ...msg,
          id: msg.id || `${msg.role}-${new Date(msg.timestamp).getTime()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
          interactive: msg.interactive ? {
            ...msg.interactive,
            callback: undefined, // Remove callback for serialization
            callbackType: msg.interactive.callbackType || 'ai', // Store callback type
          } : undefined,
        }));
      }
      updateNodeData(id, updatedData);
    },
    [id, data, updateNodeData]
  );

  const sendIntellicaCommand = useCallback(
    (commandPayload: Record<string, any>) => {
      const documentRequest = {
        'sysagent-cmd': 'NodeIntellica-Process-Started',
        'sysagent-response': '',
        timestamp: Date.now(),
        ...commandPayload,
        [id]: commandPayload,
      };
      syncNodeDataImmediate({ documentRequest, statusMessage: 'Processing Intellica command...' });
    },
    [id, syncNodeDataImmediate]
  );

  const removeLinkedNode = useCallback(
    (nodeIdToRemove: string) => {
      const updatedLinkedNodes = (data.linkedNodes || []).filter((nodeId: string) => nodeId !== nodeIdToRemove);
      syncNodeDataImmediate({ linkedNodes: updatedLinkedNodes });

      // Remove the edge connecting the Intellica node to the removed node
      setEdges((edges) =>
        edges.filter((edge) => !(edge.source === id && edge.target === nodeIdToRemove))
      );
    },
    [data.linkedNodes, syncNodeDataImmediate, setEdges, id]
  );

  // Handle command responses and ensure scoring is updated
  useEffect(() => {
    if (!data.documentRequest) return;

    const sysagentResponse = data.documentRequest['sysagent-response'];
    if (sysagentResponse === 'NodeIntellica-Process-Finished') {
      if (data.documentRequest?.response) {
        syncNodeDataImmediate({
          statusMessage: 'Intellica processing complete',
          documentRequest: null,
          intentScore: data.intentScore || 0,
          objectivesScore: data.objectivesScore || 0,
        });
      }
    } else if (sysagentResponse === 'NodeIntellica-Process-Error') {
      syncNodeDataImmediate({
        statusMessage: data.documentRequest?.error || 'Error processing Intellica command',
        documentRequest: null,
      });
    }
  }, [data.documentRequest, syncNodeDataImmediate, data.intentScore, data.objectivesScore]);

  const syncNodeData = useCallback(
    (newData: Partial<NodeIntellicaData>) => {
      if (newData.documentRequest) {
        syncNodeDataImmediate(newData);
      } else {
        syncNodeDataDebounced(newData);
      }
    },
    [syncNodeDataImmediate, syncNodeDataDebounced]
  );

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!data.isLocked}
      assignedEmails={Array.isArray(data.assignedEmails) ? data.assignedEmails : (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(data.assignedEmails) ? data.assignedEmails.join(' ') : (data.assignedEmails || ''))
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
    >
      <div className={styles.chatNodeContainer}>
        <NodeIntellicaHeader id={id} data={data} syncNodeData={syncNodeData} />
        {!data.isMinimized && !data.isLocked && (
          <div className={styles.chatContent}>
            <NodeIntellicaChat
              id={id}
              data={data}
              syncNodeData={syncNodeData}
              addNodes={addNodes}
              addEdges={addEdges}
              callbackMap={callbackMap} // Pass the callback map to reattach callbacks
            />
            <div className={styles.additionalSection}>
              {data.linkedNodes && data.linkedNodes.length > 0 && (
                <div className={styles.linkedNodesWrapper}>
                  <span>Linked Nodes:</span>
                  <ul className={styles.linkedNodesList}>
                    {data.linkedNodes.map((nodeId: string) => {
                      const linkedNode = getNode(nodeId);
                      return (
                        <li key={nodeId} className={styles.linkedNodeItem}>
                          <span>{linkedNode?.data?.label || nodeId}</span>
                          <button
                            onClick={() => removeLinkedNode(nodeId)}
                            className={styles.removeButton}
                          >
                            Remove
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div className={styles.dataInputWrapper}>
                <span>Input/Modifiers:</span>
                <div>
                  Intent Score:{' '}
                  {data.intentScore !== undefined
                    ? `${data.intentScore}%`
                    : 'N/A'}
                  {data.intentScore !== undefined && (
                    <span>
                      {' '}
                      (Clear: {data.intentScore >= 75 ? 'Yes' : 'No'})
                    </span>
                  )}
                </div>
                <div>
                  Objectives Score:{' '}
                  {data.objectivesScore !== undefined
                    ? `${data.objectivesScore}%`
                    : 'N/A'}
                  {data.objectivesScore !== undefined && (
                    <span>
                      {' '}
                      (Clear: {data.objectivesScore >= 75 ? 'Yes' : 'No'})
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.dataOutputWrapper}>
                <span>Output:</span>
                <div>
                  Intent Clarity:{' '}
                  {data.intentScore !== undefined
                    ? data.intentScore >= 75
                      ? 'Clear'
                      : 'Unclear'
                    : 'N/A'}
                </div>
                <div>
                  Objectives Clarity:{' '}
                  {data.objectivesScore !== undefined
                    ? data.objectivesScore >= 75
                      ? 'Clear'
                      : 'Unclear'
                    : 'N/A'}
                </div>
              </div>
              {data.statusMessage && (
                <div className={styles.statusMessage}>
                  {data.statusMessage}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeBaseContainer>
  );
});

export default NodeIntellica;